import _ from 'lodash';
import { useEffect, useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Modal,
  Form,
  notification,
  Button,
  Card,
  Row,
  Col,
  Space,
  Typography,
  Input,
  Alert,
  Spin
} from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  HomeOutlined,
  SearchOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

import { useGetBookingDetail } from 'hooks/useGetBookingDetail';
import useModalStore from 'stores/useModal';
import useBookingStore from 'stores/useBooking';

import { ReactComponent as IconClose } from 'assets/images/close.svg';
import 'styles/modal-move-booking.scss';

import QUERY_KEYS from 'services/api/queryKeys';
import { PermissionActionTypes } from 'helper/type/action.type';
import IBButton from 'helper/permission.action';
import { useTranslation } from 'react-i18next';
import { moveBooking } from 'services/api/module/booking.api';
import { useGetAllBooking } from 'hooks/useGetBooking';
import { BookingLine } from 'services/api/type/booking.type';

interface CurrentBookingLineData {
  bookingId: number;
  guestName: string;
  roomNo: string;
}

interface MoveBookingState {
  searchBookingId: string;
  isSearching: boolean;
  searchError: string | null;
}

function MoveBookingModal() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { bookingLineId } = useBookingStore();
  const { isOpenMoveBooking, setIsOpenMoveBooking, setInfoConfirmModal } = useModalStore();

  const [currentBookingLineData, setCurrentBookingLineData] = useState<CurrentBookingLineData>();
  const [targetBookingId, setTargetBookingId] = useState<number>();
  const [targetBookingData, setTargetBookingData] = useState<BookingLine>();
  const [moveBookingState, setMoveBookingState] = useState<MoveBookingState>({
    searchBookingId: '',
    isSearching: false,
    searchError: null
  });

  const { data: targetBookingDetail, isLoading: isLoadingTarget } = useGetAllBooking(
    {
      moved_booking_id: targetBookingId
    },
    !!targetBookingId
  );

  const { data: bookingLineDetail, isLoading: isLoadingSource } =
    useGetBookingDetail(bookingLineId);

  const { mutateAsync: mutateMoveBooking, isPending: isPendingMove } = useMutation({
    mutationFn: (params: { booking_line_ids: string; move_booking_id: number }) =>
      moveBooking(params.booking_line_ids, params.move_booking_id)
  });

  useEffect(() => {
    if (!_.isEmpty(bookingLineDetail) && isOpenMoveBooking) {
      setCurrentBookingLineData({
        bookingId: bookingLineDetail.booking_id,
        guestName: bookingLineDetail.booking_line_guests?.[0]?.guest_name || 'N/A',
        roomNo: bookingLineDetail.room_name
      });
      form.setFieldValue('booking_line_id', bookingLineDetail.booking_line_id);
    }
  }, [bookingLineDetail, isOpenMoveBooking, form]);

  useEffect(() => {
    if (targetBookingDetail && targetBookingDetail.length > 0 && targetBookingId) {
      setTargetBookingData(targetBookingDetail[0]);
      setMoveBookingState(prev => ({ ...prev, searchError: null }));
    } else if (targetBookingId && !isLoadingTarget) {
      setMoveBookingState(prev => ({
        ...prev,
        searchError: t('common.moveBookingModal.bookingNotFound')
      }));
      setTargetBookingData(undefined);
    }
  }, [targetBookingDetail, targetBookingId, isLoadingTarget, t]);

  const resetModalState = useCallback(() => {
    form.resetFields();
    setTargetBookingId(undefined);
    setCurrentBookingLineData(undefined);
    setTargetBookingData(undefined);
    setMoveBookingState({
      searchBookingId: '',
      isSearching: false,
      searchError: null
    });
  }, [form]);

  const handleCloseModal = useCallback(() => {
    setIsOpenMoveBooking(false);
    resetModalState();
  }, [setIsOpenMoveBooking, resetModalState]);

  const handleSearchBooking = useCallback(
    async (searchValue: string) => {
      if (!searchValue.trim()) {
        setMoveBookingState(prev => ({
          ...prev,
          searchError: t('common.moveBookingModal.pleaseEnterBookingId')
        }));
        return;
      }

      const bookingId = parseInt(searchValue.trim(), 10);
      if (isNaN(bookingId)) {
        setMoveBookingState(prev => ({
          ...prev,
          searchError: t('common.moveBookingModal.invalidBookingId')
        }));
        return;
      }

      if (bookingId === currentBookingLineData?.bookingId) {
        setMoveBookingState(prev => ({
          ...prev,
          searchError: t('common.moveBookingModal.cannotMoveToSameBooking')
        }));
        return;
      }

      setMoveBookingState(prev => ({ ...prev, isSearching: true, searchError: null }));
      setTargetBookingId(bookingId);
    },
    [currentBookingLineData?.bookingId, t]
  );

  const executeMove = useCallback(
    async (sourceBooking: BookingLine, targetBooking: BookingLine) => {
      try {
        await mutateMoveBooking({
          booking_line_ids: sourceBooking.booking_line_id.toString(),
          move_booking_id: targetBooking.moved_booking_id
        });

        const queriesToInvalidate = [
          [QUERY_KEYS.KEY_ROOM],
          [QUERY_KEYS.GET_TRACKING_LOGS],
          [QUERY_KEYS.GET_BOOKING_TIMELINE]
        ];

        queriesToInvalidate.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey });
        });

        notification.success({
          message: t('common.moveBookingModal.moveSuccess'),
          description: t('common.moveBookingModal.moveSuccessDescription', {
            sourceId: sourceBooking.booking_id,
            targetId: targetBooking.moved_booking_id
          })
        });

        handleCloseModal();
      } catch (err: any) {
        notification.error({
          message: t('common.moveBookingModal.moveError'),
          description: err.error || t('common.error')
        });
      }
    },
    [mutateMoveBooking, queryClient, t, handleCloseModal]
  );

  const handleMoveBookingConfirmation = useCallback(
    async (sourceBooking: BookingLine, targetBooking: BookingLine) => {
      const isChainedMove = targetBooking.booking_id !== targetBooking.moved_booking_id;

      if (isChainedMove) {
        const finalDestination = targetBooking.moved_booking_id;
        setInfoConfirmModal(true, {
          title: t('common.actions.moveBookingConfirmation'),
          message: t('common.moveBookingModal.chainedMoveConfirmation', {
            sourceId: sourceBooking.booking_id,
            targetId: targetBooking.booking_id,
            finalId: finalDestination
          }),
          onOk: async () => {
            setInfoConfirmModal(false);
            await executeMove(sourceBooking, targetBooking);
          }
        });
      } else {
        await executeMove(sourceBooking, targetBooking);
      }
    },
    [t, setInfoConfirmModal, executeMove]
  );

  const handleSubmitForm = useCallback(async () => {
    if (!bookingLineDetail || !targetBookingData) {
      notification.warning({
        message: t('common.moveBookingModal.incompleteData')
      });
      return;
    }

    await handleMoveBookingConfirmation(bookingLineDetail, targetBookingData);
  }, [bookingLineDetail, targetBookingData, handleMoveBookingConfirmation, t]);

  const isFormValid = targetBookingData && !moveBookingState.searchError;
  const isLoading = isLoadingSource || isLoadingTarget || isPendingMove;

  return (
    <Modal
      title={
        <Space align="center">
          <span>{t('common.actions.moveBooking')}</span>
        </Space>
      }
      centered
      width={800}
      open={isOpenMoveBooking}
      onCancel={handleCloseModal}
      closeIcon={<IconClose />}
      className="modal-move-booking"
      confirmLoading={isPendingMove}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCloseModal} disabled={isPendingMove}>
          {t('common.actions.cancel')}
        </Button>,
        <IBButton
          key="ok"
          className="ant-btn-secondary"
          permission={PermissionActionTypes.MOVE_BOOKING}
          onClick={handleSubmitForm}
          loading={isPendingMove}
          disabled={!isFormValid || isLoading || !bookingLineDetail || !targetBookingData}
        >
          {isPendingMove ? t('common.actions.moving') : t('common.actions.moveBooking')}
        </IBButton>
      ]}
    >
      <Card className="move-booking-card source-card">
        <div className="card-header">
          <Space align="center">
            <div className="status-indicator source-indicator" />
            <Typography.Title level={4} className="card-title">
              {t('common.moveBookingModal.sourceBooking')}
            </Typography.Title>
          </Space>
          <Typography.Text type="secondary" className="card-subtitle">
            {t('common.moveBookingModal.currentBookingInfo')}
          </Typography.Text>
        </div>

        {isLoadingSource ? (
          <div className="loading-container">
            <Spin size="small" />
            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
              {t('common.moveBookingModal.loadingBookingInfo')}
            </Typography.Text>
          </div>
        ) : (
          <Row gutter={[24, 16]} className="info-row">
            <Col span={8}>
              <Space align="center" className="info-item">
                <div className="icon-container guest-icon">
                  <UserOutlined className="icon" />
                </div>
                <div className="info-content">
                  <Typography.Text type="secondary" className="info-label">
                    {t('common.moveBookingModal.guestName')}
                  </Typography.Text>
                  <Typography.Text strong className="info-value">
                    {currentBookingLineData?.guestName || t('common.moveBookingModal.noGuest')}
                  </Typography.Text>
                </div>
              </Space>
            </Col>

            <Col span={8}>
              <Space align="center" className="info-item">
                <div className="icon-container calendar-icon">
                  <CalendarOutlined className="icon" />
                </div>
                <div className="info-content">
                  <Typography.Text type="secondary" className="info-label">
                    {t('common.moveBookingModal.bookingId')}
                  </Typography.Text>
                  <Typography.Text strong className="info-value booking-id">
                    #{currentBookingLineData?.bookingId || 'N/A'}
                  </Typography.Text>
                </div>
              </Space>
            </Col>

            <Col span={8}>
              <Space align="center" className="info-item">
                <div className="icon-container home-icon">
                  <HomeOutlined className="icon" />
                </div>
                <div className="info-content">
                  <Typography.Text type="secondary" className="info-label">
                    {t('common.moveBookingModal.roomNumber')}
                  </Typography.Text>
                  <Typography.Text strong className="info-value">
                    {currentBookingLineData?.roomNo || 'N/A'}
                  </Typography.Text>
                </div>
              </Space>
            </Col>
          </Row>
        )}
      </Card>
      <div className="search-target-section">
        <div className="arrow-container">
          <ArrowRightOutlined className="move-arrow" />
        </div>

        <Card className="move-booking-card target-card">
          <div className="card-header">
            <Space align="center">
              <div className="status-indicator target-indicator" />
              <Typography.Title level={4} className="card-title">
                {t('common.moveBookingModal.targetBooking')}
              </Typography.Title>
            </Space>
            <Typography.Text type="secondary" className="card-subtitle">
              {t('common.moveBookingModal.searchDestinationBooking')}
            </Typography.Text>
          </div>

          <Form form={form} className="search-form">
            <Form.Item
              name="booking_id"
              validateStatus={moveBookingState.searchError ? 'error' : ''}
              help={moveBookingState.searchError}
            >
              <Input
                placeholder={t('common.moveBookingModal.enterBookingIdPlaceholder')}
                className="search-input"
                size="large"
                prefix={<SearchOutlined className="search-prefix" />}
                suffix={
                  isLoadingTarget ? (
                    <Spin size="small" />
                  ) : (
                    <Button
                      type="link"
                      icon={<SearchOutlined />}
                      onClick={() => {
                        const value = form.getFieldValue('booking_id');
                        handleSearchBooking(value);
                      }}
                      className="search-button"
                      style={{ display: 'none' }}
                    >
                      {t('common.actions.search')}
                    </Button>
                  )
                }
                onPressEnter={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  const value = form.getFieldValue('booking_id');
                  handleSearchBooking(value);
                }}
                onChange={() => {
                  if (moveBookingState.searchError) {
                    setMoveBookingState(prev => ({ ...prev, searchError: null }));
                  }
                }}
              />
            </Form.Item>
          </Form>

          {targetBookingData && (
            <div className="target-booking-info">
              <Alert
                type="success"
                icon={<InfoCircleOutlined />}
                message={
                  <Typography.Text className="info-label" style={{ fontSize: 16 }}>
                    {t('common.moveBookingModal.bookingFound')}
                  </Typography.Text>
                }
                style={{ backgroundColor: 'transparent', border: 'none' }}
                description={
                  <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
                    <Col span={12}>
                      <Typography.Text type="secondary" className="info-label">
                        {t('common.moveBookingModal.guestName')}:
                      </Typography.Text>
                      <br />
                      <Typography.Text strong>
                        {targetBookingData.partner_name || t('common.moveBookingModal.noGuest')}
                      </Typography.Text>
                    </Col>
                    <Col span={12}>
                      <Typography.Text type="secondary" className="info-label">
                        {t('common.moveBookingModal.bookingId')}:
                      </Typography.Text>
                      <br />
                      <Typography.Text strong className="booking-id">
                        #{targetBookingData.booking_id}
                      </Typography.Text>
                    </Col>
                  </Row>
                }
                className="booking-found-alert"
              />

              {targetBookingData.booking_id !== targetBookingData.moved_booking_id && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('common.moveBookingModal.chainedMoveWarning')}
                  description={t('common.moveBookingModal.chainedMoveDescription', {
                    targetId: targetBookingData.booking_id,
                    finalId: targetBookingData.moved_booking_id
                  })}
                  style={{ marginTop: 12 }}
                />
              )}
            </div>
          )}
        </Card>
      </div>
    </Modal>
  );
}

export default MoveBookingModal;
